import { Back, TimelineMax, TweenMax } from 'gsap';
import Viewport from 'pixi-viewport';
import Core from '../../../Core/Core';
import { app } from '../../../index';
import { createOutlineText } from '../../../Utils/Utils';
import { HighlightEffect } from '../../Shared/HighlightEffect';
import Rectangle = PIXI.Rectangle;
import { PopupPage } from '../../Shared/PopupPage';
import { COOLDOWN, GiftFriendTab } from './GiftFriendTab';
import { ILeaderBoardPlayer } from './LeaderBoard';
import { IShareGiftData } from '../../../State/socialHelpers';

export class Gifts extends PopupPage {
    private players: ILeaderBoardPlayer[];
    private playerTabs: GiftFriendTab[] = [];
    private scrollContainer: PIXI.Container;
    private disclamer: PIXI.Text;
    private containerWidth: number;
    private containerHeight: number;
    private tabHeight: number;
    private lBContainer: Viewport;
    private fillMask: PIXI.Graphics;
    private progressText: PIXI.Text;
    private giftBcx: PIXI.Sprite;
    private timeLine: TimelineMax;
    private highlightEffect: HighlightEffect;

    constructor(
        width: number,
        height: number,
        players: ILeaderBoardPlayer[],
        curProgress: number,
        canSendGifts: boolean
    ) {
        super();

        this.containerWidth = width;
        this.containerHeight = height;

        this.scrollContainer = new PIXI.Container();

        this.players = players.filter(element => {
            return !element.isUser;
        });

        const mask = new PIXI.Graphics();
        mask.beginFill(0x000000);
        mask.drawRoundedRect(0, 0, width, height, 10);
        this.addChild(mask);
        this.scrollContainer.mask = mask;

        const disclamer = createOutlineText(
            Core.local.getText('disclamerGifts'),
            new Rectangle(width * 0.5, -height * 0.26, width, height * 0.2),
            'Baloo',
            26,
            26,
            0xffffff,
            0x006073
        );
        disclamer.anchor.set(0.5);
        disclamer.scale.set(1);
        this.disclamer = disclamer;

        const progressBg = new PIXI.Sprite(PIXI.utils.TextureCache['mapui/progressbar-bg']);
        progressBg.anchor.set(0.5);
        progressBg.x = width * 0.5;
        progressBg.y = -height * 0.15;

        const progressFill = new PIXI.Sprite(PIXI.utils.TextureCache['mapui/progressbar-fill']);
        progressFill.anchor.set(0.5);
        progressFill.x = progressBg.x;
        progressFill.y = -height * 0.15;

        const fillMask = new PIXI.Graphics();
        fillMask.beginFill(0x000000);
        fillMask.drawRect(0, 0, progressFill.width, progressFill.height);
        fillMask.x = progressFill.x - progressFill.width / 2;
        fillMask.y = progressFill.y - progressFill.height / 2;
        fillMask.scale.set(0, 1);
        progressFill.mask = fillMask;
        this.fillMask = fillMask;

        // TODO swap out for real value
        const progressText = createOutlineText(
            curProgress.toString() + '/10',
            new Rectangle(0, 0, progressFill.width, progressFill.height),
            'Baloo',
            30,
            30,
            0xffffff,
            0x991f33
        );
        progressText.anchor.set(0.5);
        progressText.x = progressFill.x;
        progressText.y = progressFill.y;
        this.progressText = progressText;

        const emoji = new PIXI.Sprite(PIXI.utils.TextureCache['mapui/giftstab-smiley']);
        emoji.anchor.set(0.5);
        emoji.x = progressBg.x - progressBg.width / 2;
        emoji.y = progressBg.y;

        const present = new PIXI.Sprite(PIXI.utils.TextureCache['mapui/leaderboard-invite-gift']);
        present.anchor.set(0.5);
        present.x = progressBg.x + progressBg.width / 2.2;
        present.y = progressBg.y;

        this.addChild(
            this.disclamer,
            progressBg,
            progressFill,
            //@ts-ignore
            fillMask,
            progressText,
            emoji,
            present
        );

        this.giftBcx = present;
        this.updateProgressBar(curProgress);
        this.createGiftAnimation();
        this.createContent(canSendGifts);
        this.createViewport().catch(err => {
            console.log(['error with gifts createviewport', err]);
        });
    }

    public cleanUp() {
        this.lBContainer.removeAllListeners();
        TweenMax.killChildTweensOf(this, true);
        if (this.highlightEffect) {
            this.highlightEffect.cleanUp();
        }
        this.lBContainer.destroy();
        this.lBContainer = null;
        this.playerTabs.forEach(t => {
            t.destroy();
        });
        this.playerTabs = [];
        this.timeLine = null;
    }

    public updateProgressBar(value: number) {
        if (value >= 10) {
            value = 10;
            if (!this.highlightEffect) {
                const highLight = (this.highlightEffect = new HighlightEffect({ scale: 0.6 }));
                highLight.position = this.giftBcx.position;
                this.removeChild(this.giftBcx);
                this.addChild(highLight);
                this.addChild(this.giftBcx);
                highLight.appear();
            }
        }
        if (value < 0) {
            value = 0;
        }
        if (value === 0) {
            this.fillMask.scale.set(0.00001, 1);
        } else {
            this.fillMask.scale.set(value / 10, 1);
        }
        this.progressText.text = value.toString() + '/10';
        this.animateGift();
    }

    public updateTabs(sentGifts: IShareGiftData[], requestedGifts: IShareGiftData[]) {
        this.players.forEach((player, index) => {
            const allSentForPlayer = sentGifts.filter(item => item.friendId === player.id);
            const allRequestedForPlayer = requestedGifts.filter(
                item => item.friendId === player.id
            );

            const lastSent = allSentForPlayer.length > 0 ? allSentForPlayer.pop().ts : null;
            const lastRequest =
                allRequestedForPlayer.length > 0 ? allRequestedForPlayer.pop().ts : null;

            let waitingFor = null;
            if (lastSent && !lastRequest) {
                waitingFor = 'sent';
            } else if (!lastSent && lastRequest) {
                waitingFor = 'request';
            } else if (lastSent && lastRequest) {
                const firstOne = lastSent < lastRequest ? lastSent : lastRequest;
                const lastOne = lastSent > lastRequest ? lastSent : lastRequest;

                // if values for both
                // use earliest unless!...newest started after last ran out
                let toUse = firstOne;
                if (lastOne - firstOne > COOLDOWN) {
                    toUse = lastOne;
                }

                waitingFor = toUse === lastRequest ? 'request' : 'sent';
            }

            this.playerTabs[index].updateButtonTime(lastSent, lastRequest, waitingFor);
        });
    }

    private createContent(canSendGifts: boolean) {
        this.tabHeight = this.containerHeight * 0.18;
        // this.addChild(this.scrollContainer);
        this.players.forEach((player, index) => {
            const playerTab = new GiftFriendTab(
                player,
                this.containerWidth,
                this.containerHeight * 0.15,
                false,
                canSendGifts
            );
            playerTab.x = this.containerWidth * 0.975;
            playerTab.y = index * this.tabHeight + this.tabHeight * 0.6;
            this.playerTabs.push(playerTab);
            this.scrollContainer.addChild(playerTab);
        });
    }

    private async createViewport() {
        // we cannot use viewport here and then not remove it ever...cleanup function needed for when popup is removed
        const wHeight =
            this.players.length <= 5
                ? this.containerHeight
                : this.players.length * this.tabHeight + this.tabHeight * 0.1;
        const lBContainer = new Viewport({
            screenWidth: this.containerWidth,
            screenHeight: this.containerHeight,
            worldWidth: this.containerWidth,
            worldHeight: wHeight,
            interaction: app.renderer.plugins.interaction,
        });
        this.lBContainer = lBContainer;
        this.lBContainer.drag().decelerate();

        // set boundaries of scrollable leaderboard
        this.lBContainer.clamp({
            left: true,
            right: true,
            top: true,
            bottom: this.lBContainer.worldHeight,
        });
        this.lBContainer.on('drag-start', () => {
            this.playerTabs.forEach(element => {
                element.setDraggin(true);
            });
        });

        this.lBContainer.on('drag-end', () => {
            this.playerTabs.forEach(element => {
                element.setDraggin(false);
            });
        });
        this.addChild(this.lBContainer);
        this.lBContainer.addChild(this.scrollContainer);
    }

    private createGiftAnimation() {
        const giftBox = this.giftBcx;
        const timeLine = (this.timeLine = new TimelineMax());
        timeLine.pause(0);
        timeLine.to(giftBox.scale, 0.1, { x: 1.2, y: 1.2, ease: Back.easeOut.config(0.2) });
        timeLine.to(giftBox, 0.07, { rotation: 0.1, ease: Back.easeOut.config(0.1) }, '-=0.1');
        timeLine.to(giftBox, 0.07, { rotation: -0.1, ease: Back.easeOut.config(0.1) });
        timeLine.to(giftBox, 0.07, { rotation: 0.1, ease: Back.easeOut.config(0.1) }, '-=0.1');
        timeLine.to(giftBox, 0.07, { rotation: 0, ease: Back.easeOut.config(0.1) });
        timeLine.to(giftBox.scale, 0.1, { x: 1, y: 1 }, '-=.05');
    }

    private animateGift() {
        if (!this.giftBcx || !this.timeLine) {
            return;
        }
        this.timeLine.restart();
    }
}
